/**
 * @file    useEngineSubtypeSwitch.ts
 * @scope   ENTERPRISE
 * @summary Resets the engine slice to the new descriptor's initial state when the user switches
 *          between two engine subtypes (e.g. Enterprise → CPR).
 * @consumes EnterpriseContext `dispatch`, active descriptor
 * @produces useEngineSubtypeSwitch
 * @sideEffects Dispatches `RESET` on subtype → subtype changes only.
 * @notes   Legacy ↔ engine switches are untouched (still handled by `useChartTypeChangeEffect`).
 *          With a single registered subtype this never fires; it is the seam CPR will use (§9).
 */
import { useEffect, useRef } from 'react';
import { ActiveEngineDescriptor } from './useActiveEngineDescriptor';
import { useActiveEngineSlice } from './useActiveEngineSlice';

export const useEngineSubtypeSwitch = (descriptor: ActiveEngineDescriptor): void => {
  // WHY the active slice's dispatch: each subtype owns its provider, so reset the one being entered.
  const { dispatch } = useActiveEngineSlice(descriptor);
  const previousIdRef = useRef<string | undefined>(descriptor?.id);

  useEffect(() => {
    const previousId = previousIdRef.current;
    previousIdRef.current = descriptor?.id;
    // WHY both defined: only subtype → subtype must reshape; entering/leaving the engine is legacy's job.
    if (!descriptor || !previousId || previousId === descriptor.id) return;
    dispatch({ type: 'RESET', state: descriptor.stateSlice.initialState() });
  }, [descriptor, dispatch]);
};

export default useEngineSubtypeSwitch;
